<template>
    <div
        :id="item.id"
        :aria-labelledby="`${item.id}-tab`"
        role="tabpanel"
        class="tab-pane fade"
    >
        <section class="fst-panel">
            <FormTabHeader
                icon="fal fa-tasks"
                :title="item.name"
                :subtitle="statusSubtitle"
            >
                <template #actions>
                    <button class="fst-create-btn" @click="createNewStatus">
                        <i class="fal fa-plus"></i>
                        <span>{{ $t('forms.status.create') }}</span>
                    </button>
                </template>
            </FormTabHeader>

            <table class="fst-table">
                <thead>
                    <tr>
                        <th class="fst-th fst-th--narrow">{{ $t('forms.status.icon') }}</th>
                        <th class="fst-th">{{ $t('forms.status.name') }}</th>
                        <th class="fst-th">{{ $t('forms.status.default') }}</th>
                        <th class="fst-th">{{ $t('forms.status.active') }}</th>
                        <th class="fst-th fst-th--right">{{ $t('forms.status.actions') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="status in form.statuses"
                        :key="status.id"
                        class="fst-row"
                        @click="openDrawer(status)"
                    >
                        <td class="fst-td">
                            <span class="fst-icon" :class="{ 'fst-icon--muted': !status.active }">
                                <i :class="status.icon"></i>
                            </span>
                        </td>
                        <td class="fst-td fst-td--name">
                            {{ status.name }}
                        </td>
                        <td class="fst-td">
                            <span class="fst-pill" :class="status.default ? 'fst-pill--primary' : 'fst-pill--muted'">
                                {{ status.default ? $t('common.yes') : $t('common.no') }}
                            </span>
                        </td>
                        <td class="fst-td">
                            <span class="fst-pill" :class="status.active ? 'fst-pill--success' : 'fst-pill--danger'">
                                {{ status.active ? $t('common.yes') : $t('common.no') }}
                            </span>
                        </td>
                        <td class="fst-td fst-td--actions">
                            <div class="fst-actions">
                                <button
                                    class="fst-btn fst-btn--ghost"
                                    @click.stop="editStatus(status)"
                                >
                                    <i class="fal fa-edit"></i>
                                    <span>{{ $t('common.edit') }}</span>
                                </button>
                                <button
                                    class="fst-btn"
                                    :class="status.active ? 'fst-btn--danger' : 'fst-btn--success'"
                                    @click.stop="toggleActive(status)"
                                >
                                    <i :class="status.active ? 'fal fa-ban' : 'fal fa-check'"></i>
                                    <span>{{ status.active ? $t('forms.status.deactivate') : $t('forms.status.activate') }}</span>
                                </button>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </section>

        <!-- Status Detail Drawer -->
        <transition name="fst-drawer">
            <div
                v-if="drawerOpen"
                class="fst-drawer-overlay"
                @mousedown.self="drawerOpen = false"
            >
                <aside class="fst-drawer" role="dialog" aria-modal="true">
                    <button
                        type="button"
                        class="fst-drawer-close"
                        :aria-label="$t('common.close') || 'Fermer'"
                        @click="drawerOpen = false"
                    >
                        <i class="fal fa-times"></i>
                    </button>
                    <div class="p-5" v-if="drawerStatus">
                <div class="d-flex align-items-center mb-4">
                    <div
                        class="d-flex align-items-center justify-content-center rounded-circle bg-blue-100 mr-3"
                        style="width: 48px; height: 48px"
                    >
                        <i :class="drawerStatus.icon" class="text-blue-600 text-xl"></i>
                    </div>
                    <div>
                        <h4 class="mb-0 font-bold text-gray-800 text-lg">{{ drawerStatus.name }}</h4>
                    </div>
                </div>

                <hr class="my-4" />

                <div class="space-y-4">
                    <!-- Translations -->
                    <div>
                        <h6 class="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                            {{ $t('forms.status.name') }}
                        </h6>
                        <div
                            v-for="translation in drawerStatus.translations"
                            :key="translation.locale"
                            class="d-flex align-items-center mb-2"
                        >
                            <span class="uppercase text-xs font-bold text-blue-500 mr-2" style="min-width: 24px">
                                {{ translation.locale }}
                            </span>
                            <span class="text-sm text-gray-700">{{ translation.name }}</span>
                        </div>
                    </div>

                    <!-- Icon -->
                    <div>
                        <h6 class="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                            {{ $t('forms.status.icon') }}
                        </h6>
                        <div class="d-flex align-items-center">
                            <div
                                class="d-flex align-items-center justify-content-center rounded bg-gray-100 border mr-2"
                                style="width: 36px; height: 36px"
                            >
                                <i :class="drawerStatus.icon" class="text-blue-500"></i>
                            </div>
                            <code class="text-xs text-gray-500">{{ drawerStatus.icon }}</code>
                        </div>
                    </div>

                    <!-- Flags -->
                    <div>
                        <h6 class="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                            {{ $t('forms.status.properties') }}
                        </h6>
                        <div class="d-flex flex-column" style="gap: 8px">
                            <div class="d-flex justify-content-between align-items-center">
                                <span class="text-sm text-gray-600">{{ $t('forms.status.default') }}</span>
                                <span
                                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                                    :class="drawerStatus.default ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'"
                                >
                                    {{ drawerStatus.default ? $t('common.yes') : $t('common.no') }}
                                </span>
                            </div>
                            <div class="d-flex justify-content-between align-items-center">
                                <span class="text-sm text-gray-600">{{ $t('forms.status.final') }}</span>
                                <span
                                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                                    :class="drawerStatus.final ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'"
                                >
                                    {{ drawerStatus.final ? $t('common.yes') : $t('common.no') }}
                                </span>
                            </div>
                            <div class="d-flex justify-content-between align-items-center">
                                <span class="text-sm text-gray-600">{{ $t('forms.status.active') }}</span>
                                <span
                                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                                    :class="drawerStatus.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
                                >
                                    {{ drawerStatus.active ? $t('common.yes') : $t('common.no') }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <!-- Versions Timeline -->
                    <div class="mt-4">
                        <h6 class="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
                            {{ $t('forms.status.versions') }}
                        </h6>

                        <div v-if="versionsLoading" class="fst-versions-loading">
                            <BmvLoader :active="true" />
                        </div>

                        <div v-else-if="versions.length === 0" class="text-center py-4">
                            <i class="fal fa-history text-gray-300 text-2xl mb-2"></i>
                            <p class="text-sm text-gray-400">{{ $t('forms.status.no_versions') }}</p>
                        </div>

                        <div v-else class="version-timeline">
                            <div
                                v-for="version in versions"
                                :key="version.id"
                                class="version-entry"
                            >
                                <div class="version-dot"></div>
                                <div class="version-content">
                                    <div class="d-flex justify-content-between align-items-start mb-1">
                                        <div>
                                            <span class="text-xs text-gray-400">
                                                {{ new Date(version.created_at).toLocaleString() }}
                                            </span>
                                            <div v-if="version.user" class="text-sm text-gray-600">
                                                {{ $t('forms.status.version_by') }} <span class="font-medium text-gray-800">{{ version.user.name }}</span>
                                            </div>
                                        </div>
                                        <button
                                            class="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded text-gray-600 bg-white hover:bg-gray-50 focus:outline-none"
                                            @click="restoreVersion(version)"
                                        >
                                            <i class="fal fa-undo mr-1"></i>
                                            {{ $t('forms.status.version_restore') }}
                                        </button>
                                    </div>
                                    <!-- Diffs -->
                                    <div v-if="filteredChanges(version).length" class="mt-1">
                                        <div
                                            v-for="(change, i) in filteredChanges(version)"
                                            :key="i"
                                            class="text-xs text-gray-500 mb-1"
                                        >
                                            <span class="font-medium">{{ formatFieldName(change.field) }}</span>:
                                            <span class="text-red-400 line-through">{{ formatValue(change.old) }}</span>
                                            &rarr;
                                            <span class="text-green-600">{{ formatValue(change.new) }}</span>
                                        </div>
                                    </div>
                                    <!-- Snapshot fallback (first version, no diffs) -->
                                    <div v-else-if="version.snapshot" class="mt-1">
                                        <div
                                            v-for="(value, key) in filteredSnapshot(version.snapshot)"
                                            :key="key"
                                            class="text-xs text-gray-500 mb-1"
                                        >
                                            <span class="font-medium">{{ formatFieldName(key) }}</span>:
                                            <span class="text-green-600">{{ formatSnapshotValue(key, value) }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
                </aside>
            </div>
        </transition>

        <!-- Edit / Create Status Modal (V2) -->
        <BmvModal v-model="modalOpen" size="md" :closable="true" @close="cancel">
            <template #header>
                <div class="fst-modal-head" v-if="editingStatus">
                    <span
                        class="fst-modal-head-icon"
                        :class="isCreating ? 'fst-modal-head-icon--success' : 'fst-modal-head-icon--primary'"
                    >
                        <i :class="editingStatus.icon || 'fal fa-plus'"></i>
                    </span>
                    <div class="fst-modal-head-body">
                        <h2 class="fst-modal-head-title">
                            {{ isCreating ? $t('forms.status.create_title') : $t('forms.status.edit_title') }}
                        </h2>
                        <span class="fst-modal-head-sub" v-if="!isCreating">{{ editingStatus.translations[langs[0]] }}</span>
                    </div>
                </div>
            </template>

            <div class="fst-modal-body" v-if="editingStatus">
                <div
                    v-for="lang in langs"
                    :key="lang"
                    class="fst-field"
                >
                    <label class="fst-field-label">
                        {{ $t('forms.status.name') }}
                        <span class="fst-field-lang">{{ lang }}</span>
                    </label>
                    <input
                        v-model="editingStatus.translations[lang]"
                        type="text"
                        class="fst-input"
                    />
                </div>

                <div class="fst-field">
                    <label class="fst-field-label">{{ $t('forms.status.icon') }}</label>
                    <IconPicker
                        :value="editingStatus.icon"
                        @input="editingStatus.icon = $event"
                    />
                </div>

                <div class="fst-toggles">
                    <label class="fst-toggle-row">
                        <span class="fst-toggle-label">{{ $t('forms.status.default') }}</span>
                        <span class="fst-switch">
                            <input type="checkbox" v-model="editingStatus.default" />
                            <span class="fst-switch-track"></span>
                        </span>
                    </label>
                    <label class="fst-toggle-row">
                        <span class="fst-toggle-label">{{ $t('forms.status.final') }}</span>
                        <span class="fst-switch">
                            <input type="checkbox" v-model="editingStatus.final" />
                            <span class="fst-switch-track"></span>
                        </span>
                    </label>
                    <label class="fst-toggle-row">
                        <span class="fst-toggle-label">{{ $t('forms.status.active') }}</span>
                        <span class="fst-switch">
                            <input type="checkbox" v-model="editingStatus.active" />
                            <span class="fst-switch-track"></span>
                        </span>
                    </label>
                </div>
            </div>

            <template #footer>
                <BmvButton variant="ghost" size="md" @click="cancel">
                    {{ $t('forms.status.cancel') }}
                </BmvButton>
                <BmvButton variant="primary" size="md" :loading="loading" icon="fal fa-save" @click="saveStatus">
                    {{ $t('forms.status.save') }}
                </BmvButton>
            </template>
        </BmvModal>
    </div>
</template>

<script>
import FormMixin from './FormMixin'
import FormTabHeader from './FormTabHeader'
import BmvModal from '@/components/UI/v2/BmvModal.vue'
import BmvButton from '@/components/UI/v2/BmvButton.vue'
import BmvLoader from '@/components/UI/v2/BmvLoader.vue'
import IconPicker from '@/components/UI/IconPicker.vue'
import { mapActions, mapState } from 'vuex'

export default {
    name: 'FormStatus',
    mixins: [FormMixin],
    components: { BmvModal, BmvButton, BmvLoader, FormTabHeader, IconPicker },
    watch: {
        drawerOpen(open) {
            if (typeof document === 'undefined') return
            if (open) {
                document.addEventListener('keydown', this.onDrawerKey)
            } else {
                document.removeEventListener('keydown', this.onDrawerKey)
            }
        }
    },
    beforeDestroy() {
        if (typeof document !== 'undefined') {
            document.removeEventListener('keydown', this.onDrawerKey)
        }
    },
    props: ['item'],
    data() {
        return {
            modalOpen: false,
            editingStatus: null,
            isCreating: false,
            loading: false,
            drawerOpen: false,
            drawerStatus: null,
            versions: [],
            versionsLoading: false
        }
    },
    computed: {
        ...mapState({ langs: (state) => state.langs }),
        statusSubtitle() {
            if (!this.form || !this.form.statuses) return ''
            return `${this.form.statuses.length} ${this.$t('forms.parametrage.status_count') || 'statuts'}`
        }
    },
    methods: {
        ...mapActions('form', ['createStatus', 'updateStatus', 'getForm', 'getStatusVersions', 'restoreStatusVersion']),
        onDrawerKey(e) {
            if (e.key === 'Escape') this.drawerOpen = false
        },
        openDrawer(status) {
            this.drawerStatus = status
            this.drawerOpen = true
            this.fetchVersions(status.id)
        },
        fetchVersions(statusId) {
            this.versionsLoading = true
            this.versions = []
            this.getStatusVersions({ formId: this.form.id, statusId })
                .then((response) => {
                    this.versions = response.data.versions || response.data
                })
                .catch((error) => {
                    this.$store.dispatch('dispatchError', error)
                })
                .finally(() => {
                    this.versionsLoading = false
                })
        },
        filteredChanges(version) {
            if (!version.changes) return []
            const ignored = ['actions']
            return version.changes.filter((c) => !ignored.includes(c.field))
        },
        formatFieldName(field) {
            const labels = {
                active: this.$t('forms.status.active'),
                default: this.$t('forms.status.default'),
                final: this.$t('forms.status.final'),
                icon: this.$t('forms.status.icon'),
                name: this.$t('forms.status.name')
            }
            return labels[field] || field
        },
        formatValue(value) {
            if (value === true) return this.$t('common.yes')
            if (value === false) return this.$t('common.no')
            if (value === null || value === undefined) return '—'
            if (typeof value === 'object') return JSON.stringify(value)
            return String(value)
        },
        filteredSnapshot(snapshot) {
            const ignored = ['actions', 'id']
            const result = {}
            Object.keys(snapshot).forEach((key) => {
                if (!ignored.includes(key)) result[key] = snapshot[key]
            })
            return result
        },
        formatSnapshotValue(key, value) {
            if (key === 'translations' && typeof value === 'object') {
                return Object.entries(value)
                    .map(([locale, trans]) => `${locale.toUpperCase()}: ${trans.name || trans}`)
                    .join(', ')
            }
            return this.formatValue(value)
        },
        restoreVersion(version) {
            this.$bmvDialog
                .confirm({
                    title: this.$t('forms.status.version_restore_confirm'),
                    variant: 'primary'
                })
                .then((ok) => {
                    if (!ok) return
                    this.restoreStatusVersion({
                        formId: this.form.id,
                        statusId: this.drawerStatus.id,
                        versionId: version.id
                    })
                        .then(() => {
                            this.getForm(this.form.id).then((form) => {
                                const updated = form.statuses.find((s) => s.id === this.drawerStatus.id)
                                if (updated) this.drawerStatus = updated
                            })
                            this.fetchVersions(this.drawerStatus.id)
                            this.$store.dispatch('toast', {
                                type: 'success',
                                title: this.$t('forms.status.version_restored')
                            })
                        })
                        .catch((error) => {
                            this.$store.dispatch('dispatchError', error)
                        })
                })
        },
        createNewStatus() {
            const translations = {}
            this.langs.forEach((lang) => {
                translations[lang] = ''
            })
            this.editingStatus = {
                icon: '',
                default: false,
                final: false,
                active: true,
                translations
            }
            this.isCreating = true
            this.modalOpen = true
        },
        editStatus(status) {
            const translations = {}
            this.langs.forEach((lang) => {
                const t = status.translations.find((tr) => tr.locale === lang)
                translations[lang] = t ? t.name : ''
            })
            this.editingStatus = {
                id: status.id,
                icon: status.icon,
                default: status.default,
                final: status.final,
                active: status.active,
                translations
            }
            this.isCreating = false
            this.modalOpen = true
        },
        async toggleActive(status) {
            if (status.active) {
                const ok = await this.$bmvDialog.confirm({
                    title: this.$t('forms.status.deactivate_confirm_title') || 'Désactiver ce statut ?',
                    message: this.$t('forms.status.deactivate_confirm_message') || 'Ce statut ne sera plus sélectionnable dans les tickets existants ou futurs.',
                    variant: 'danger',
                    confirmLabel: this.$t('forms.status.deactivate'),
                    cancelLabel: this.$t('common.cancel')
                })
                if (!ok) return
            }

            const translations = {}
            this.langs.forEach((lang) => {
                const t = status.translations.find((tr) => tr.locale === lang)
                translations[lang] = t ? t.name : ''
            })
            this.updateStatus({
                formId: this.form.id,
                status: {
                    id: status.id,
                    icon: status.icon,
                    default: status.default,
                    final: status.final,
                    active: !status.active,
                    translations
                }
            })
                .then(() => {
                    this.getForm(this.form.id)
                    this.$store.dispatch('toast', {
                        type: 'success',
                        title: this.$t('success.title')
                    })
                })
                .catch((error) => {
                    this.$store.dispatch('dispatchError', error)
                })
        },
        cancel() {
            this.modalOpen = false
            this.$nextTick(() => {
                this.editingStatus = null
                this.isCreating = false
            })
        },
        saveStatus() {
            this.loading = true
            const action = this.isCreating ? 'createStatus' : 'updateStatus'
            this[action]({
                formId: this.form.id,
                status: this.editingStatus
            })
                .then(() => {
                    this.getForm(this.form.id)
                    this.$store.dispatch('toast', {
                        type: 'success',
                        title: this.$t('success.title')
                    })
                    this.cancel()
                })
                .catch((error) => {
                    this.$store.dispatch('dispatchError', error)
                })
                .finally(() => {
                    this.loading = false
                })
        }
    }
}
</script>

<style scoped>
/* Drawer */
.fst-drawer-overlay {
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.45);
    display: flex;
    justify-content: flex-end;
    z-index: 1100;
    backdrop-filter: blur(2px);
}

.fst-drawer {
    position: relative;
    width: min(440px, 100vw);
    height: 100%;
    background: var(--v2-surface-0);
    box-shadow: -8px 0 32px rgba(15, 23, 42, 0.12);
    overflow-y: auto;
}

.fst-drawer-close {
    position: absolute;
    top: 10px;
    right: 10px;
    width: 32px;
    height: 32px;
    border-radius: var(--v2-radius-sm);
    background: transparent;
    border: none;
    color: var(--v2-text-3);
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all var(--v2-transition-fast);
    z-index: 1;
}

.fst-drawer-close:hover {
    background: var(--v2-surface-2);
    color: var(--v2-text-1);
}

.fst-versions-loading {
    position: relative;
    min-height: 100px;
}

/* Transition */
.fst-drawer-enter-active,
.fst-drawer-leave-active {
    transition: opacity 200ms ease;
}

.fst-drawer-enter-active .fst-drawer,
.fst-drawer-leave-active .fst-drawer {
    transition: transform 240ms cubic-bezier(0.22, 1, 0.36, 1);
}

.fst-drawer-enter,
.fst-drawer-leave-to {
    opacity: 0;
}

.fst-drawer-enter .fst-drawer,
.fst-drawer-leave-to .fst-drawer {
    transform: translateX(100%);
}

.fst-create-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 32px;
    padding: 0 12px;
    border: 1px solid var(--v2-primary);
    border-radius: var(--v2-radius-sm);
    background: var(--v2-primary);
    color: var(--v2-primary-contrast, #fff);
    font: inherit;
    font-size: 0.82rem;
    font-weight: var(--v2-font-weight-medium);
    line-height: 1.2;
    white-space: nowrap;
    cursor: pointer;
    box-sizing: border-box;
    transition: background var(--v2-transition-fast), border-color var(--v2-transition-fast);
}

.fst-create-btn:hover {
    background: var(--v2-primary-hover);
    border-color: var(--v2-primary-hover);
}

.fst-create-btn i {
    font-size: 0.78rem;
    color: inherit !important;
}

.fst-panel {
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    overflow: hidden;
    margin-top: var(--v2-space-5);
}

.fst-table {
    width: 100%;
    border-collapse: collapse;
    font-size: var(--v2-text-sm-size);
}

.fst-th {
    text-align: left;
    padding: 10px 18px;
    font-size: 0.7rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-4);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    background: var(--v2-surface-1);
    border-bottom: 1px solid var(--v2-border);
    white-space: nowrap;
}

.fst-th--narrow {
    width: 72px;
}

.fst-th--right {
    text-align: right;
}

.fst-row {
    cursor: pointer;
    transition: background var(--v2-transition-fast);
}

.fst-row:not(:last-child) .fst-td {
    border-bottom: 1px solid var(--v2-border-subtle);
}

.fst-row:hover {
    background: var(--v2-surface-1);
}

.fst-td {
    padding: 12px 18px;
    color: var(--v2-text-2);
    vertical-align: middle;
}

.fst-td--name {
    color: var(--v2-text-1);
    font-weight: var(--v2-font-weight-medium);
}

.fst-td--actions {
    text-align: right;
    white-space: nowrap;
}

.fst-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: var(--v2-radius-sm);
    background: var(--v2-primary-soft);
    color: var(--v2-primary);
}

.fst-icon i {
    font-size: 0.9rem;
}

.fst-icon--muted {
    background: var(--v2-surface-2);
    color: var(--v2-text-4);
}

.fst-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 22px;
    padding: 0 10px;
    border-radius: 999px;
    font-size: 0.72rem;
    font-weight: 500;
    line-height: 1;
    letter-spacing: 0.01em;
    white-space: nowrap;
    box-sizing: border-box;
}

.fst-pill--success {
    background: rgba(16, 185, 129, 0.12);
    color: #047857;
}

.fst-pill--primary {
    background: rgba(8, 145, 178, 0.12);
    color: #0e7490;
}

.fst-pill--muted {
    background: var(--v2-surface-2);
    color: var(--v2-text-3);
}

.fst-pill--danger {
    background: rgba(239, 68, 68, 0.12);
    color: #b91c1c;
}

.fst-actions {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.fst-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 28px;
    padding: 0 10px;
    border: 1px solid transparent;
    border-radius: var(--v2-radius-sm);
    font: inherit;
    font-size: 0.76rem;
    font-weight: var(--v2-font-weight-medium);
    line-height: 1.2;
    cursor: pointer;
    box-sizing: border-box;
    white-space: nowrap;
    transition: background var(--v2-transition-fast), border-color var(--v2-transition-fast), color var(--v2-transition-fast);
}

.fst-btn i {
    font-size: 0.72rem;
}

.fst-btn--ghost {
    border-color: var(--v2-border);
    background: var(--v2-surface-0);
    color: var(--v2-text-2);
}

.fst-btn--ghost:hover {
    background: var(--v2-surface-2);
    color: var(--v2-text-1);
}

.fst-btn--success {
    background: rgba(16, 185, 129, 0.12);
    color: #047857;
}

.fst-btn--success:hover {
    background: rgba(16, 185, 129, 0.2);
}

.fst-btn--danger {
    background: rgba(239, 68, 68, 0.1);
    color: #b91c1c;
}

.fst-btn--danger:hover {
    background: rgba(239, 68, 68, 0.18);
}

@media (max-width: 900px) {
    .fst-head {
        flex-direction: column;
        align-items: stretch;
    }

    .fst-create-btn {
        justify-content: center;
    }

    .fst-card {
        overflow-x: auto;
    }

    .fst-th, .fst-td {
        padding: 10px 12px;
    }

    .fst-btn span {
        display: none;
    }

    .fst-btn {
        padding: 0 8px;
    }
}

/* Modal (V2) */
.fst-modal-head {
    display: flex;
    align-items: center;
    gap: var(--v2-space-3);
    flex: 1;
    min-width: 0;
}

.fst-modal-head-icon {
    width: 44px;
    height: 44px;
    border-radius: var(--v2-radius-md);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.1rem;
    flex-shrink: 0;
}

.fst-modal-head-icon--primary {
    background: var(--v2-primary-soft);
    color: var(--v2-primary);
}

.fst-modal-head-icon--success {
    background: rgba(16, 185, 129, 0.12);
    color: #047857;
}

.fst-modal-head-icon i {
    color: inherit !important;
}

.fst-modal-head-body {
    min-width: 0;
    display: flex;
    flex-direction: column;
}

.fst-modal-head-title {
    margin: 0;
    font-size: 1rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
    letter-spacing: -0.005em;
    line-height: 1.3;
}

.fst-modal-head-sub {
    font-size: var(--v2-text-xs-size);
    color: var(--v2-text-3);
    margin-top: 2px;
}

.fst-modal-body {
    display: flex;
    flex-direction: column;
    gap: var(--v2-space-4);
}

.fst-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.fst-field-label {
    font-size: 0.8rem;
    font-weight: var(--v2-font-weight-medium);
    color: var(--v2-text-2);
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.fst-field-lang {
    font-size: 0.62rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--v2-primary);
    background: var(--v2-primary-soft);
    padding: 2px 6px;
    border-radius: var(--v2-radius-xs, 4px);
}

.fst-input {
    width: 100%;
    height: 34px;
    padding: 0 12px;
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-sm);
    font-family: inherit;
    font-size: 0.85rem;
    color: var(--v2-text-1);
    background: var(--v2-surface-0);
    outline: none;
    box-sizing: border-box;
    transition: border-color var(--v2-transition-fast), box-shadow var(--v2-transition-fast);
}

.fst-input:focus {
    border-color: var(--v2-primary) !important;
    box-shadow: 0 0 0 3px rgba(8, 145, 178, 0.12) !important;
    outline: none !important;
}

.fst-toggles {
    display: flex;
    flex-direction: column;
    gap: var(--v2-space-2);
}

.fst-toggle-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--v2-space-3) var(--v2-space-4);
    background: var(--v2-surface-1);
    border: 1px solid var(--v2-border-subtle);
    border-radius: var(--v2-radius-md);
    cursor: pointer;
    margin: 0;
}

.fst-toggle-label {
    font-size: 0.85rem;
    color: var(--v2-text-1);
    font-weight: var(--v2-font-weight-medium);
}

.fst-switch {
    position: relative;
    display: inline-block;
    width: 36px;
    height: 20px;
    flex-shrink: 0;
}

.fst-switch input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
}

.fst-switch-track {
    position: absolute;
    inset: 0;
    border-radius: 999px;
    background: var(--v2-surface-3, #cbd5e1);
    transition: background var(--v2-transition-base);
}

.fst-switch-track::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 16px;
    height: 16px;
    background: #fff;
    border-radius: 50%;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.15);
    transition: transform var(--v2-transition-base);
}

.fst-switch input:checked + .fst-switch-track {
    background: var(--v2-primary);
}

.fst-switch input:checked + .fst-switch-track::after {
    transform: translateX(16px);
}

.fst-switch input:focus-visible + .fst-switch-track {
    box-shadow: 0 0 0 3px rgba(8, 145, 178, 0.2);
}

.version-timeline {
    position: relative;
    padding-left: 20px;
    border-left: 2px solid #e5e7eb;
}

.version-entry {
    position: relative;
    padding-bottom: 16px;
    margin-bottom: 4px;
}

.version-entry:last-child {
    padding-bottom: 0;
}

.version-dot {
    position: absolute;
    left: -25px;
    top: 4px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: #3b82f6;
    border: 2px solid white;
    box-shadow: 0 0 0 2px #e5e7eb;
}

.version-content {
    background: #f9fafb;
    border-radius: 8px;
    padding: 10px 12px;
    border: 1px solid #f3f4f6;
}
</style>
